/**
 * Zentrales Register aller globalen Tastenkürzel. Einzige Quelle für den
 * Spickzettel („?"), den Palette-Fuß und die kbd-Hinweise. Die eigentlichen
 * Handler leben weiterhin dort, wo sie wirken (App-Shell, Sidebar, Editor) —
 * hier stehen nur ihre Beschreibungen, damit Kürzel auffindbar & konsistent
 * bleiben. Neue globale Kürzel bitte hier eintragen.
 *
 * Tasten stehen abstrakt drin (`mod`, `alt`, `shift`, `enter`, `esc`) und
 * werden erst beim Anzeigen je System beschriftet — ⌘ auf dem Mac, Strg unter
 * Windows (`platformKeys.ts`). Nie ⌘, ⌥ oder „Strg" fest eintragen.
 */
export interface ShortcutDef {
  /** Tasten in Anzeige-Reihenfolge, z. B. ['mod', 'K'] oder ['?']. */
  keys: readonly string[];
  label: string;
  group: string;
}

/** Tastenfolgen, die außer im Spickzettel auch als Hinweis an Knöpfen und Menüs stehen. */
export const SHORTCUT_KEYS = {
  openSearch: ['mod', 'K'],
  closeTab: ['alt', 'W'],
  reopenTab: ['alt', 'shift', 'T'],
  save: ['mod', 'S'],
  copy: ['mod', 'C'],
  paste: ['mod', 'V'],
  paletteBackgroundTab: ['mod', 'enter'],
  /** Seitenleiste „Belegdaten" im Beleg-Editor (Alt+I = „Info"; W und Ziffern gehören der Tab-Leiste). */
  toggleDocumentDetails: ['alt', 'I'],
} as const satisfies Record<string, readonly string[]>;

export const SHORTCUT_GROUPS = ['Allgemein', 'Navigation', 'Tabs', 'Beleg-Editor', 'Projekt anlegen'] as const;

export const SHORTCUTS: ShortcutDef[] = [
  { keys: SHORTCUT_KEYS.openSearch, label: 'Suche & Befehle öffnen', group: 'Allgemein' },
  { keys: ['?'], label: 'Diese Kürzel-Übersicht anzeigen', group: 'Allgemein' },
  { keys: ['mod', 'B'], label: 'Seitenleiste ein-/ausklappen', group: 'Navigation' },
  { keys: ['#'], label: 'In der Palette: nur Befehle statt Suche', group: 'Navigation' },
  { keys: ['↑', '↓'], label: 'In der Palette: Eintrag wählen', group: 'Navigation' },
  { keys: ['enter'], label: 'In der Palette: Auswahl öffnen/ausführen', group: 'Navigation' },
  { keys: SHORTCUT_KEYS.paletteBackgroundTab, label: 'In der Palette: Treffer in neuem Tab im Hintergrund öffnen', group: 'Navigation' },
  { keys: ['→'], label: 'In der Palette: in die Aktionen des Treffers (auch Tab; zurück mit ← oder Esc)', group: 'Navigation' },
  { keys: SHORTCUT_KEYS.copy, label: 'In der Palette: Nummer des Treffers kopieren', group: 'Navigation' },
  { keys: ['esc'], label: 'Dialog/Palette schließen', group: 'Navigation' },
  { keys: ['mod', 'Klick'], label: 'Link in neuem Tab im Hintergrund öffnen (auch Mittelklick)', group: 'Tabs' },
  { keys: ['alt', '1…8'], label: 'Zum Tab 1 bis 8 wechseln', group: 'Tabs' },
  { keys: ['alt', '9'], label: 'Zum letzten Tab wechseln', group: 'Tabs' },
  { keys: SHORTCUT_KEYS.closeTab, label: 'Aktuellen Tab schließen', group: 'Tabs' },
  { keys: SHORTCUT_KEYS.reopenTab, label: 'Zuletzt geschlossenen Tab wiederherstellen', group: 'Tabs' },
  { keys: SHORTCUT_KEYS.save, label: 'Beleg speichern', group: 'Beleg-Editor' },
  { keys: SHORTCUT_KEYS.toggleDocumentDetails, label: 'Seitenleiste „Belegdaten" ein-/ausblenden', group: 'Beleg-Editor' },
  { keys: ['enter'], label: 'Nächste Position (im Positionsraster)', group: 'Beleg-Editor' },
  { keys: ['enter'], label: 'In der Artikel-Spalte: Artikel-Suche öffnen', group: 'Beleg-Editor' },
  { keys: ['Bild ↑', 'Bild ↓'], label: 'In der Artikel-Suche: Trefferliste blättern', group: 'Beleg-Editor' },
  { keys: ['alt', '↑', '↓'], label: 'In der Artikel-Suche: Warengruppe wechseln', group: 'Beleg-Editor' },
  { keys: SHORTCUT_KEYS.paste, label: 'Positionen aus Excel einfügen', group: 'Beleg-Editor' },
  { keys: ['mod', 'shift', 'enter'], label: 'Neue Position — Fokus direkt in die Artikelsuche', group: 'Beleg-Editor' },
  { keys: ['mod', 'shift', 'D'], label: 'Aktuelle Position duplizieren', group: 'Beleg-Editor' },
  { keys: ['mod', 'shift', 'X'], label: 'Aktuelle Position löschen', group: 'Beleg-Editor' },
  { keys: ['mod', 'shift', 'H'], label: 'Überschrift einfügen', group: 'Beleg-Editor' },
  { keys: ['mod', 'shift', 'B'], label: 'Seitenumbruch einfügen', group: 'Beleg-Editor' },
  { keys: ['mod', 'shift', '↓'], label: 'Ins Positionsraster springen', group: 'Beleg-Editor' },
  { keys: ['enter'], label: 'Nächstes Feld (in Such-Feldern: markierten Vorschlag übernehmen)', group: 'Projekt anlegen' },
  { keys: ['enter'], label: 'Im Kunden- oder Ursprungsfeld ohne Markierung: große Liste mit dem Suchtext öffnen', group: 'Projekt anlegen' },
  { keys: ['mod', 'enter'], label: 'Projekt anlegen bzw. speichern', group: 'Projekt anlegen' },
  { keys: ['alt', 'K'], label: 'Zum Kunden springen', group: 'Projekt anlegen' },
  { keys: ['alt', 'U'], label: 'Zum Ursprungsprojekt springen', group: 'Projekt anlegen' },
  { keys: ['alt', 'P'], label: 'Zur Projektleitung springen', group: 'Projekt anlegen' },
  { keys: ['alt', 'L'], label: 'Zum Liefertermin springen', group: 'Projekt anlegen' },
  { keys: ['alt', 'M'], label: 'Weitere Angaben auf-/zuklappen', group: 'Projekt anlegen' },
  { keys: ['alt', 'C'], label: 'Projekt-Code selbst vergeben', group: 'Projekt anlegen' },
  { keys: ['backspace'], label: 'In der Projektleitung: letzte Person entfernen', group: 'Projekt anlegen' },
];

/** Kürzel nach Gruppe, in der Reihenfolge von `SHORTCUT_GROUPS`. */
export function shortcutsByGroup(): { group: string; items: ShortcutDef[] }[] {
  return SHORTCUT_GROUPS.map((group) => ({
    group,
    items: SHORTCUTS.filter((shortcut) => shortcut.group === group),
  })).filter((section) => section.items.length > 0);
}

/** Tippt der Nutzer gerade in ein Eingabefeld? (Dann greifen die globalen Kürzel nicht.) */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || !element.tagName) return false;
  return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT' || element.isContentEditable;
}
